import {
  AbstractMesh,
  Color3,
  CubeTexture,
  DirectionalLight,
  HemisphericLight,
  InstancedMesh,
  RenderTargetTexture,
  Scene,
  ShadowGenerator,
  Vector3,
} from "@babylonjs/core";
import { isTouchDevice } from "../util/device.js";

/**
 * Scene-wide lighting rig for the "believable mall" look:
 *  - an IBL environment texture so PBR materials get real ambient/reflections
 *  - a low hemispheric fill (the old bright hemi washed everything flat)
 *  - one directional "sun through the skylight" with a STATIC shadow map.
 *
 * The mall never moves, so the shadow map renders once at load
 * (REFRESHRATE_RENDER_ONCE) and is free every frame after that; even phones
 * can afford it. Dynamic things (avatars) use cheap blob shadows instead of
 * the map, so the map never needs a per-frame refresh.
 */

export const SHADOW_MAP_SIZE_DESKTOP: number = 2048;
export const SHADOW_MAP_SIZE_TOUCH: number = 1024;

/** Hard ceiling on per-material light count. Babylon's glTF loader raises
 *  maxSimultaneousLights on EVERY scene material to scene.lights.length when
 *  a load completes ("making sure we enable enough lights"); with the mall's
 *  ~29 point lights that makes skinned avatar shaders declare one uniform
 *  block per light and blow past GL_MAX_VERTEX_UNIFORM_BUFFERS on mobile
 *  GPUs (observed live at a 14-block limit; phones commonly allow 12). Four
 *  lights = sun + fill + the two nearest accents, which is all anything in
 *  the mall visually needs. */
export const MAX_LIGHTS_PER_MATERIAL: number = 4;

/** Re-clamp every scene material after a glTF load (see above). Safe to call
 *  often; only touches materials the loader raised past the cap. */
export function clampMaterialLights(scene: Scene): void {
  for (const material of scene.materials) {
    const lit = material as { maxSimultaneousLights?: number };
    if (typeof lit.maxSimultaneousLights === "number" && lit.maxSimultaneousLights > MAX_LIGHTS_PER_MATERIAL) {
      lit.maxSimultaneousLights = MAX_LIGHTS_PER_MATERIAL;
    }
  }
}

/** Materials that must never darken the world: the emissive skylight plane
 *  would eclipse the whole atrium, neon strips are pure light sources, and
 *  contact-shadow decals are already fake shadows. */
const NON_CASTER_MATERIALS: Set<string> = new Set([
  "atrium-sky-mat",
  "mm-neon",
  "mm-contact-shadow",
]);

export function shadowMapSize(touch: boolean): number {
  return touch ? SHADOW_MAP_SIZE_TOUCH : SHADOW_MAP_SIZE_DESKTOP;
}

/** Opaque, visible, real geometry casts; glass/holo tubes (alpha < 1),
 *  invisible collision hulls, and the NON_CASTER_MATERIALS set do not. */
export function shouldCastShadow(mesh: AbstractMesh): boolean {
  if (!mesh.isVisible || mesh.material === null || mesh.getTotalVertices() === 0) {
    return false;
  }
  if (mesh.material.alpha < 1) {
    return false;
  }
  return !NON_CASTER_MATERIALS.has(mesh.material.name);
}

/** Everything with a lit material samples the shadow map; without this the
 *  sun ADDS full daylight to meshes it should not reach (washed-out shops).
 *  Instances are excluded: receiveShadows only lives on their SOURCE mesh
 *  (the prop library sets it there), and setting it here just logs a
 *  Babylon warning per instance. */
export function shouldReceiveShadow(mesh: AbstractMesh): boolean {
  if (mesh instanceof InstancedMesh) {
    return false;
  }
  if (mesh.material === null || mesh.getTotalVertices() === 0) {
    return false;
  }
  return !NON_CASTER_MATERIALS.has(mesh.material.name);
}

export interface MallLightingOptions {
  /** Prefiltered .env texture URL; null skips IBL (unit tests / NullEngine). */
  iblUrl: string | null;
  /** Override the touch-device probe (tests). */
  touch?: boolean;
}

export class MallLighting {
  readonly sun: DirectionalLight;
  readonly fill: HemisphericLight;
  readonly shadows: ShadowGenerator;
  private readonly scene: Scene;

  constructor(scene: Scene, options: MallLightingOptions) {
    this.scene = scene;
    const touch: boolean = options.touch === undefined ? isTouchDevice() : options.touch;

    if (options.iblUrl !== null) {
      const env: CubeTexture = CubeTexture.CreateFromPrefilteredData(options.iblUrl, scene);
      scene.environmentTexture = env;
      scene.environmentIntensity = 0.5;
    }

    // Soft ambient fill only: shape now comes from the sun + IBL, not from an
    // omnidirectional wash. Screenshot-tuned live on 2026-07-13: any higher
    // and the sun shadows saturate to invisible on the near-white floors.
    this.fill = new HemisphericLight("mall-fill", new Vector3(0, 1, 0), scene);
    this.fill.intensity = 0.18;
    this.fill.diffuse = new Color3(1.0, 1.0, 1.0);
    this.fill.groundColor = new Color3(0.32, 0.34, 0.37);

    // Daylight through the atrium skylight, tilted so pillars/trees/benches
    // throw readable shadows instead of sitting on none.
    this.sun = new DirectionalLight("mall-sun", new Vector3(-0.35, -1, -0.22).normalize(), scene);
    this.sun.position = new Vector3(30, 40, 20);
    this.sun.intensity = 1.7;
    this.sun.diffuse = new Color3(1.0, 0.98, 0.94);
    this.sun.specular = new Color3(1.0, 0.98, 0.94);
    this.sun.autoCalcShadowZBounds = true;

    this.shadows = new ShadowGenerator(shadowMapSize(touch), this.sun);
    this.shadows.bias = 0.0006;
    this.shadows.normalBias = 0.02;
    this.shadows.darkness = 0;
    // PCF needs WebGL2 shadow samplers; Babylon silently falls back to
    // Poisson sampling on engines without them, so no version branch needed.
    this.shadows.usePercentageCloserFiltering = true;
    this.shadows.filteringQuality = touch
      ? ShadowGenerator.QUALITY_LOW
      : ShadowGenerator.QUALITY_MEDIUM;
  }

  /**
   * Call once AFTER the whole static world is built (zones merge their shells
   * during build): registers every qualifying mesh as caster + receiver and
   * freezes the map to a single render. Streamed shop interiors only need
   * receiveShadows (their ceilings already occlude the sun in the static
   * map), so streaming never re-renders it.
   */
  applyToWorld(): void {
    clampMaterialLights(this.scene);
    for (const mesh of this.scene.meshes) {
      if (shouldReceiveShadow(mesh)) {
        mesh.receiveShadows = true;
      }
      if (shouldCastShadow(mesh)) {
        this.shadows.addShadowCaster(mesh, false);
      }
    }
    const map: RenderTargetTexture | null = this.shadows.getShadowMap();
    if (map !== null) {
      map.refreshRate = RenderTargetTexture.REFRESHRATE_RENDER_ONCE;
    }
  }

  /** Re-render the static map once (only needed if static geometry changes). */
  refreshShadows(): void {
    const map: RenderTargetTexture | null = this.shadows.getShadowMap();
    if (map !== null) {
      map.refreshRate = RenderTargetTexture.REFRESHRATE_RENDER_ONCE;
    }
  }
}

export function setupMallLighting(scene: Scene, options: MallLightingOptions): MallLighting {
  return new MallLighting(scene, options);
}
